import { Card, CircularProgress, Typography } from "@mui/material";

import { useRouter } from "next/navigation";
import price from "@/utils/price";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import Tooltip from "@mui/material/Tooltip";
import { useLingui } from "@lingui/react";
import { t, Trans } from "@lingui/macro";
import { useAlertStore } from "@/components/context/alert-store";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import LoginToSaveProduct from "@/components/core/alert-messages/login-warning";
import AddToCartSuccessMessage from "@/components/core/alert-messages/add-cart-success";
import Cookies from "js-cookie";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ImageWithFallback from "./image-with-fallback";
import Link from "next/link";
import { sendGAEventTrigger } from "@/utils/analytics-trigger";
import { decode } from "html-entities";
import { useState } from "react";
import { baseUrl } from "@/utils/api-definition";
import { getRequest, postRequest } from "@/utils/request";
interface ItemProps {
  item: {
    id: number;
    name: string;
    subtitle: string;
    type: string;
    original_image: string;
    seo_url: string;
    price: number;
    special: number;
  };
  lang: string;
  mobileGridStyle?: string;
  setErrors?: (errors: boolean) => void;
}

const ProductSingle = ({ lang, item, mobileGridStyle, setErrors }: ItemProps) => {
  const router = useRouter();
  const { i18n } = useLingui();
  const { setMessage } = useAlertStore();
  const queryClient = useQueryClient();
  const { status } = useSession();
  const productUrl = `/product/${item.seo_url}`;
  const productImage = `${baseUrl}/image/${baseUrl ? item.original_image.replace(baseUrl, "").replace("image/", "") : item.original_image}`;

  const cookieKey = status === "unauthenticated" ? "anon-wishlist" : "wishlist";
  const wishlistRaw = Cookies.get(cookieKey);
  let wishlistArray: string[] = [];

  if (wishlistRaw) {
    try {
      wishlistArray = JSON.parse(wishlistRaw);
    } catch {
      wishlistArray = [];
    }
  }

  const productId = item.id.toString();
  const addedToWishlist = wishlistArray.some((wishlistItem: any) => wishlistItem === productId);
  const [isAddingToCart, setIsAddingToCart] = useState<boolean>(false);
  const addToCartMutation = useMutation({
    mutationFn: () =>
      postRequest({
        path: { name: "cart" },
        body: { product_id: item.id, quantity: 1, option: {} },
        lang,
      }),
    onSuccess: (data) => {
      setIsAddingToCart(false);
      if (data.success === 0) {
        if (data.error_with_key) {
          setMessage({
            text: t(
              i18n,
            )`Some required fields are missing. Please complete them before proceeding.`,
            severity: "warning",
          });
        }
        setErrors?.(true);
        router.push(`/product/${item.seo_url}`);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["cartQuantity"] });
      setMessage({
        text: <AddToCartSuccessMessage item={item} />,
        severity: "success",
      });
      setErrors?.(false);
    },
    onError: () => {
      setIsAddingToCart(false);
      router.push(productUrl);
    },
  });

  const addToWishlistMutation = useMutation({
    mutationFn: () =>
      postRequest({
        path: { name: "wishlist", id: String(item.id) },
        lang,
      }),
    onSuccess: () => {
      setMessage({
        text: (
          <Trans>
            Success: You have added{" "}
            <Link
              prefetch={false}
              href={productUrl}
              style={{ color: "#19a1b3", textDecoration: "underline" }}
            >
              {item.name}
            </Link>{" "}
            to your{" "}
            <Link
              prefetch={false}
              href={"/account/wishlist"}
              style={{ color: "#19a1b3", textDecoration: "underline" }}
            >
              wishlist
            </Link>
            !
          </Trans>
        ),
        severity: "success",
      });
    },
    onError: () => {
      setMessage({
        text: t(i18n)`An unexpected error occurred. Please try again later.`,
        severity: "error",
      });
    },
  });
  const removeFromWishlistMutation = useMutation({
    mutationFn: () =>
      postRequest({
        path: { name: "wishlist", id: String(item.id) },
        method: "DELETE",
        lang,
      }),
    onSuccess: () => {
      setMessage({
        text: (
          <Trans>
            Success: You have deleted{" "}
            <Link
              prefetch={false}
              href={productUrl}
              style={{ color: "#19a1b3", textDecoration: "underline" }}
            >
              {item.name}
            </Link>{" "}
            from your{" "}
            <Link
              prefetch={false}
              href={"/account/wishlist"}
              style={{ color: "#19a1b3", textDecoration: "underline" }}
            >
              wishlist
            </Link>
            !
          </Trans>
        ),
        severity: "success",
      });
    },
    onError: () => {
      setMessage({
        text: t(i18n)`An unexpected error occurred. Please try again later.`,
        severity: "error",
      });
    },
  });
  const handleAddToCart = async (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    if (isAddingToCart) return;
    setIsAddingToCart(true);
    try {
      const productData = await getRequest({
        path: { name: "products", id: String(item.id) },
        lang,
      });
      const hasViewRequired = productData?.data?.offerz_product_tabs?.some(
        (tab: { view_required: number }) => tab.view_required === 1,
      );

      if (hasViewRequired) {
        router.push(`/product/${item.seo_url}`);
        setIsAddingToCart(false);
      } else {
        addToCartMutation.mutate();
        sendGAEventTrigger("Add To Cart", {
          send_to: `${process.env.NEXT_PUBLIC_GTM_MEASUREMENT_ID}`,
          category: "ecommerce",
          label: item.name,
        });
      }
    } catch (error) {
      setIsAddingToCart(false);
      console.error("Error fetching product data:", error);
    }
  };

  const handleAddToWishlist = async (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    if (status === "unauthenticated") {
      setMessage({
        text: <LoginToSaveProduct item={{ id: productId, name: item.name }} />,
        severity: "warning",
      });
    }
    const addedToWishlist = wishlistArray.some((wishlistItem: any) => wishlistItem === productId);
    if (addedToWishlist) {
      if (status !== "unauthenticated") {
        removeFromWishlistMutation.mutate();
      }

      wishlistArray = wishlistArray.filter(function (item: string) {
        return item !== productId;
      });
    } else {
      if (status !== "unauthenticated") {
        addToWishlistMutation.mutate();
      }
      sendGAEventTrigger("Add To Wishlist", {
        send_to: `${process.env.NEXT_PUBLIC_GTM_MEASUREMENT_ID}`,
        label: item.name,
        category: "ecommerce",
      });
      wishlistArray.push(productId);
    }

    if (status === "unauthenticated") {
      Cookies.set("anon-wishlist", JSON.stringify(wishlistArray));
    } else {
      Cookies.set("wishlist", JSON.stringify(wishlistArray));
    }
  };

  return (
    <Link href={productUrl} prefetch={false}>
      <Card
        onClick={() => router.push(productUrl)}
        className="flex cursor-pointer flex-col items-start justify-start"
        sx={{
          boxShadow: "0 5px 18px 0 rgba(40, 43, 47, 0.2)",
          borderRadius: "15px",
          color: "#000",
          "&:hover": {
            boxShadow: "0 7px 19px 0 rgb(40 43 47 / 32%)",
          },
        }}
      >
        <div
          className={`flex justify-center self-center ${mobileGridStyle === "grid" ? "max-h-[166px]" : "w-full"}`}
        >
          <ImageWithFallback
            fallBackImageClassName="h-[120px] max-h-[120px] md:h-[166px] md:max-h-[166px]"
            className={`${mobileGridStyle === "grid" ? "h-[120px] max-h-[120px] md:h-auto md:!max-h-[140px]" : ""}`}
            src={productImage}
            alt=""
            width={250}
            height={166}
          />
        </div>

        <div
          className={`px-5 md:px-[25px] ${mobileGridStyle === "grid" ? "min-h-[120px] pb-2" : "min-h-auto pb-0 md:min-h-[120px] md:pb-2"} mt-2.5`}
        >
          {item.special === 0 || item.special === null ? (
            <Typography sx={{ fontSize: { xs: "16px", md: "18px" }, mb: "6px", fontWeight: 600 }}>
              {price(Number(item.price))}
            </Typography>
          ) : (
            <Typography
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "flex-start",
                flexDirection: { xs: mobileGridStyle === "grid" ? "column" : "row", md: "row" },
                gap: { xs: mobileGridStyle === "grid" ? "1px" : "4px", md: "4px" },
                color: "red",
                fontSize: { xs: "16px", md: "18px" },
                mb: "6px",
              }}
            >
              {price(Number(item.special))}
              <Typography
                component="span"
                variant="body1"
                sx={{
                  textDecoration: "line-through",
                  color: "red",
                  fontSize: "12px",
                }}
              >
                {price(Number(item.price))}
              </Typography>
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: { xs: "16px", md: "18px" },
              display: "-webkit-box",
              overflow: "hidden",
              textOverflow: "ellipsis",
              WebkitLineClamp: "3",
              WebkitBoxOrient: "vertical",
              opacity: "0.8",
              transition: ".3s ease",
              wordBreak: "break-word",
              "&:hover": { color: "primary.main" },
            }}
          >
            {decode(item.name)}
          </Typography>
        </div>
        <Box sx={{ ml: "auto", display: "flex", gap: 1, px: { xs: "20px", md: "25px" }, pb: 2 }}>
          <Tooltip
            arrow
            placement="top"
            title={addedToWishlist ? t(i18n)`Remove from wish list` : t(i18n)`Add to wish list`}
          >
            <IconButton
              onClick={(event) => handleAddToWishlist(event)}
              data-testid="product-wishlist"
              disableRipple
              size="small"
              sx={{
                broderRadius: "100%",
                bgcolor: "#eee",
                height: "40px",
                width: "40px",
                color: addedToWishlist ? "primary.main" : "#000",
                "&:hover": {
                  bgcolor: "primary.main",
                  color: "#fff",
                },
              }}
            >
              {addedToWishlist ? (
                <FavoriteIcon sx={{ fontSize: "inherit" }} />
              ) : (
                <FavoriteBorderIcon sx={{ fontSize: "inherit" }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip arrow placement="top" title={t(i18n)`Buy`}>
            <IconButton
              onClick={(event) => handleAddToCart(event)}
              disableRipple
              aria-label="cart"
              size="small"
              sx={{
                broderRadius: "100%",
                bgcolor: "#eee",
                height: "40px",
                width: "40px",
                color: "#000",
                "&:hover": {
                  bgcolor: "primary.main",
                  color: "#fff",
                },
                "&:hover .MuiCircularProgress-circle": {
                  color: "white",
                },
              }}
            >
              {isAddingToCart ? (
                <CircularProgress size={12} />
              ) : (
                <ShoppingCartOutlinedIcon sx={{ fontSize: "inherit" }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Card>
    </Link>
  );
};

export default ProductSingle;
